<template>
  <div class="full-size">
    <div
      class="preview-container"
      v-if="webAppDetail">
      <v-tabs
        v-model="rightColumnTabIndex"
        mobile-break-point="2"
        class="frame">
        <v-tabs-slider />
        <v-tab
          v-for="(column, index) in relevantItems"
          :key="index">
          <v-icon
            v-if="column.icon"
            style="padding-right: 5px ">
            {{ column.icon }}
          </v-icon>
          {{ column.name }}
        </v-tab>
        <v-tabs-items>
          <v-tab-item
            v-for="(column, index) in relevantItems"
            :key="index">
            <component
              v-if="rightColumnTabIndex === index"
              :is="column.component"
              class="tab-items" />
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
      <v-card-actions>
        <div class="icon-container">
          <v-icon small>
            update
          </v-icon>
          {{ (webAppDetail.modifiedOn || webAppDetail.createdOn) | moment("from") }}
          <v-btn
            flat
            icon
            class="views"
            :disabled="true">
            <v-icon>fas fa-eye</v-icon>
          </v-btn>
          <span class="icon-text">
            {{ webAppDetail.views }}
          </span>
          <span :class="likeClasses">
            <v-btn
              flat
              icon
              @click="doVote(true)"
              :value="true">
              <v-icon>thumb_up</v-icon>
            </v-btn>
            <span class="icon-text">
              {{ webAppDetail.like }}
            </span>
          </span>
          <span :class="dislikeClasses">
            <v-btn
              flat
              icon
              @click="doVote(false)"
              :value="false">
              <v-icon>thumb_down</v-icon>
            </v-btn>
            <span class="icon-text">
              {{ webAppDetail.dislike }}
            </span>
          </span>
        </div>
        <v-spacer />
        <v-pagination
          v-if="rightColumnTabIndex == 0"
          v-model="screenIndex"
          :disabled="true"
          :length="paginationLength"
          :total-visible="paginationLength"
          circle />
        <v-spacer />
        <slot name="commands">
          <div class="top-level-buttons">
            <v-btn
              v-if="!isGuest"
              icon
              class="share-button"
              @click="showGuestDialog = true"
              :title="$t('serverModel.components.webAppPreview.share', 'Share')"
              flat>
              <v-icon>fas fa-share</v-icon>
            </v-btn>
          </div>
        </slot>
      </v-card-actions>
      <GuestDialog :show.sync="showGuestDialog" />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { webAppDataSource } from '../services'
import { webAppSchema, bizViewSchema } from '../model'
import { modulesIconMixin } from '../mixins/modulesIcon'
import VueFriendlyIframe from 'vue-friendly-iframe'
import { random } from 'faker'
import GuestDialog from './GuestDialog'
import axios from 'axios'
import { apiVersion } from '../constants'

const arrayChunks = (array, chunkSize) => Array(Math.ceil(array.length / chunkSize))
  .fill()
  .map((_, index) => index * chunkSize)
  .map(begin => array.slice(begin, begin + chunkSize))

function sleep (ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export default {
  components: {
    GuestDialog,
    VueFriendlyIframe
  },
  props: {
    onDelete: {
      type: Function,
      default: null
    },
    extends: {
      type: Boolean,
      default: false
    }
  },
  mixins: [modulesIconMixin],
  data () {
    return {
      schema: webAppSchema,
      bizViewSchema: bizViewSchema,
      dataSource: webAppDataSource,
      webAppDetail: null,
      pageLength: 3,
      pageIndex: 0,
      showGuestDialog: false,
      isExtends: this.extends,
      rightColumnTabIndex: 0,
      panel: [
        {
          name: this.$t('serverModel.components.webAppPreview.preview', 'Preview'),
          icon: 'fas fa-desktop',
          component: () => import(/* webpackChunkName: "Preview" */ '../components/Preview')
        },
        {
          name: this.$t('serverModel.components.webAppPreview.comment', 'Comment'),
          icon: 'fa fa-comment',
          component: () => import(/* webpackChunkName: "Commentary" */ '../components/Commentary')
        },
        {
          name: this.$t('serverModel.components.webAppPreview.share', 'Share'),
          icon: 'fas fa-share',
          component: () => import(/* webpackChunkName: "Share" */ '../components/Share'),
          filter ({ isGuest }) {
            return isGuest !== true
          }
        },
        {
          name: this.$t('serverModel.components.webAppPreview.recentActivity', 'Recent Activity'),
          icon: 'update',
          component: () => import(/* webpackChunkName: "RecentActivity" */ '../components/History')
        },
        {
          name: this.$t('serverModel.components.webAppPreview.detail', 'Detail'),
          icon: 'description',
          component: () => import(/* webpackChunkName: "Detail" */ '../components/DetailPanel'),
          filter ({ isGuest }) {
            return isGuest !== true
          }
        }
      ]
    }
  },
  computed: {
    ...mapState('authentication', [
      'token',
      'instance'
    ]),
    ...mapState('youtubeLayout', [
      'showRightColumn'
    ]),
    ...mapState('schema-view', [
      'panelOptionsDefinition'
    ]),
    ...mapState('sidebar', [
      'showSidebar',
      'autoClose'
    ]),
    ...mapGetters('authentication', [
      'isGuest'
    ]),
    ...mapState('search', [
      'index'
    ]),
    itemId () {
      return this.$route.params.id
    },

    item () {
      return this.itemId && this.index[this.itemId]
    },
    container () {
      return this.item && this.item.getContainer()
    },
    relevantItems () {
      return (this.panel && this.panel.filter(c => c.component && (!c.filter || c.filter({ isGuest: this.isGuest })))) || this.metaRightColumns
    },
    screens () {
      return this.webAppDetail ? this.webAppDetail.screens.content : []
    },
    screenPages () {
      return arrayChunks(this.screens, this.pageLength)
    },
    currentScreen () {
      return this.screens && this.screenIndex != null && this.screens[this.screenIndex]
    },
    screenUrl () {
      return this.currentScreen && this.currentScreen.url
    },
    webAppUrl () {
      return this.webAppDetail && this.webAppDetail.url && `${this.instance}${this.webAppDetail.url}`
    },
    url () {
      return `${this.screenUrl ? this.screenUrl : this.webAppUrl}&jwttoken=${this.token}`
    },
    screenIndex: {
      get () {
        return this.$route.query.screenIndex != null ? parseInt(this.$route.query.screenIndex) : null
      },
      set (screenIndex) {
        this.$router.push({
          ...this.$route,
          query: {
            ...this.$route.query,
            screenIndex
          }
        })
      }
    },
    webAppPreviewId () {
      return this.$route.query.webAppPreviewId
    },
    paginationLength () {
      return this.$vuetify.breakpoint.xsOnly ? 1 : 3
    },
    likeClasses () {
      return {
        'like': true,
        'current-user-vote': this.webAppDetail && this.webAppDetail.currentUserVote === true,
        'has-more-votes': this.webAppDetail && this.webAppDetail.like > this.webAppDetail.dislike
      }
    },
    dislikeClasses () {
      return {
        'dislike': true,
        'current-user-vote': this.webAppDetail && this.webAppDetail.currentUserVote === false,
        'has-more-votes': this.webAppDetail && this.webAppDetail.like < this.webAppDetail.dislike
      }
    }
  },
  methods: {
    ...mapActions(['setMainTitle']),
    ...mapActions('schema-view', [
      'setPanelOptionsDefinition'
    ]),
    ...mapActions('sidebar', [
      'setDisplay',
      'setAutoClose'
    ]),
    ...mapActions('search', [
      'navigateToPanelId'
    ]),
    ...mapActions('schema-view', [
      'displayDefinition'
    ]),
    ...mapActions('notification', [
      'notify',
      'error'
    ]),
    ...mapActions('youtubeLayout', [
      'toggleDisplayRightColumn'
    ]),
    next () {
      this.pageIndex = this.pageIndex + 1 === this.screenPages.length
        ? 0
        : this.pageIndex + 1
    },
    prev () {
      this.pageIndex = this.pageIndex - 1 < 0
        ? this.screenPages.length - 1
        : this.pageIndex - 1
    },
    fav () {
      return random.boolean()
    },
    navigateToBizviews () {
      this.navigateToPanelId({
        id: 86,
        query: {
          webAppId: this.webAppDetail.id
        }
      })
    },
    showItem () {
      this.displayDefinition({
        schema: this.schema,
        item: this.webAppDetail,
        fetch: true
      })
    },
    navigateToWebApp () {
      this.navigateToPanelId({
        id: 174
      })
    },
    navigateToWebAppEdit () {
      this.showItem()
      this.navigateToPanelId({
        id: 174
      })
    },
    deleteItem (definition) {
      const self = this
      this.$dialog.confirm({
        text: this.$t('serverModel.components.webAppPreview.deleteMesssage', 'Do you really want to delete {label}?', { label: definition.webAppDetail.label }),
        title: this.$t('serverModel.components.webAppPreview.deleteTitle', 'Delete {label}', { label: definition.webAppDetail.label }),
        actions: {
          false: this.$t('serverModel.components.webAppPreview.no', 'No'),
          true: {
            color: 'red',
            text: this.$t('serverModel.components.webAppPreview.yes', 'Yes I do'),
            async handle () {
              await sleep(500)
              await definition.dataSource.delete(definition.webAppDetail.id)
              self.notify(this.$t('serverModel.components.webAppPreview.confirmDelete', 'Item {label} deleted', { label: definition.webAppDetail.label }))
              self.selectedItem = null
              self.navigateToWebApp()
            }
          }
        }
      })
    },
    async doVote (doesLike) {
      const response = await axios.post(`${this.instance}/counters/like/webapp/${this.webAppDetail.id}/${doesLike}`, null, {
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Accept': apiVersion
        }
      })
      if (response && response.data && this.webAppDetail) {
        Object.assign(this.webAppDetail, response.data)
      }
    },
    extendPreview () {
      this.isExtends = !this.isExtends
      if (this.showRightColumn && this.isExtends) {
        this.toggleDisplayRightColumn()
      }
      if (!this.autoClose && this.isExtends) {
        this.setAutoClose(!this.autoClose)
      }
    }
  },
  provide () {
    return {
      autoClose: this.autoClose
    }
  },
  watch: {
    webAppPreviewId: {
      immediate: true,
      async handler (webAppPreviewId) {
        if (webAppPreviewId) {
          this.webAppDetail = await webAppDataSource.detail(webAppPreviewId)
        } else {
          this.webAppDetail = null
        }
      }
    },
    webAppDetail: {
      immediate: true,
      handler (webAppDetail) {
        if (webAppDetail) {
          this.setPanelOptionsDefinition({
            schema: webAppDataSource.schema,
            item: webAppDetail
          })
          if (webAppDetail.label) {
            this.setMainTitle(webAppDetail.label)
          }
        }
      }
    }
  }
}
</script>

<style lang="scss">
  @import "~common-assets/scss/we";
  $fav_color: #FFD800;

  .add-screen-btn{
    background: rgb(254, 50, 102);
    margin-left: auto;
  }

  .preview-container {
    @extend .full-size;
    @extend .flex-column;
    padding: 5px;
    position: relative;
    background-color: #fff;
    border-color: #fff;
    color: rgba(0,0,0,.87);
    box-shadow: 0 2px 1px -1px rgba(0,0,0,.2), 0 1px 1px 0 rgba(0,0,0,.14), 0 1px 3px 0 rgba(0,0,0,.12);
    .tab-items{
      height: 100%;
      box-shadow: none;
    }
    .icon-container {
      .like, .dislike, .views {
        margin-left: 5px;
      }
      .like, .dislike, .like .v-btn, .dislike .v-btn {
        color: rgb(128, 128, 128)
      }
      .has-more-votes {
        font-weight: bold;
      }
      .like.has-more-votes, .like.current-user-vote, .like.current-user-vote .v-btn {
        color: rgb(0, 192, 0)
      }
      .dislike.like.has-more-votes, .dislike.current-user-vote, .dislike.current-user-vote .v-btn {
        color: rgb(192, 0, 0)
      }
    }
    .top-level-buttons {
      position: relative;
      @extend .flex-row;
      @extend .text-no-wrap;
      .share-button {
        color: rgb(128, 128, 128);
        margin-left: 5px;
      }
      .icon-text {
        padding-top: 8px;
      }
    }
    .frame {
      border-width: 1px;
      @extend .full-size;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3), 0 0 40px rgba(0, 0, 0, 0.1) inset;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      iframe {
        @extend .full-size;
        border-width: 1px;
        border-style: inherit;
        //width: 400px;
      }
    }
    .text-center {
      margin: 0 auto;
      padding-left: 150px;
    }
    .not-publish {
      text-align: center;
      margin-top: 50px;
    }

    .tile-item {
      height: 50px;
      margin: 5px;
      position: relative;
      overflow: hidden;
    }

    .controls {
      position: absolute;
      top: 6px;
      left: 0;
      right: 0;
      text-align: center;
      z-index: 100;

      .previous {
        left: -20px;
        position: absolute;
      }

      .next {
        right: -20px;
        position: absolute;
      }
    }
    .card-preview{
      margin: 0 15px;
      margin-bottom: 5px;
      border-top: 1px solid #eee;
      padding: 3px 0 0;
      height: 43px;
      margin-top: 7px;
      .card-preview-screen{
        float: right;
        width: 100px;
      }
    }
    .fav-icon {
      right: 0;
      z-index: 3;
      color: $fav_color;
    }

    .content-menu {
      padding: 0;
      background: white;
      max-width: 45px;
    }
    .preview-favicon{
      float: right;
      margin-right: 10px;
      margin-bottom: 5px;
    }
    .card-title {
      font-size: 17px;
      color: black;
      padding: 0;
      display: flex;
      justify-content: flex-end;
      .card-icon {
        margin-right: 8px;

        .v-icon {
          color: black;
        }
      }
    }
    .v-window{
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      .v-window__container{
        height: 100%;
        .v-window-item{
          height: 100%;
        }
      }
    }
  }
</style>
